import React, { useCallback, useEffect, useState } from 'react';

import { DownloadFormat } from '../../../../../../backend/src/types/types';
import { Checkbox } from '../../shadcn/checkbox';
import {
  MultiSelect,
  MultiSelectItem,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectTrigger,
  MultiSelectValue,
} from '../../shadcn/multi-select';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
} from '@/new/components/shadcn/select';

const FORMAT_OPTIONS = [DownloadFormat.EXCEL, DownloadFormat.DOCX, DownloadFormat.CSV];

interface Props {
  columnOptions: string[];
  downloadTitle: string;
  loadingDownload: boolean;
  totalCount?: number;
  showSourceDocumentsOption?: boolean;
  handleDownload: ({
    title,
    columns,
    downloadFormat,
    includeSourceDocuments,
  }: {
    title: string;
    columns: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
  }) => void;
}

export const DownloadTableModal = ({
  columnOptions,
  downloadTitle,
  loadingDownload,
  totalCount,
  showSourceDocumentsOption = false,
  handleDownload,
}: Props) => {
  const [title, setTitle] = useState(downloadTitle);
  const [columns, setColumns] = useState<string[]>(columnOptions);
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>(FORMAT_OPTIONS[0]);
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);

  useEffect(() => {
    setTitle(downloadTitle);
  }, [downloadTitle]);

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleChangeSelectedColumns = useCallback((values: string[]) => {
    setColumns(values);
  }, []);

  const handleChangeSelectedFormat = useCallback((newValue: string) => {
    setDownloadFormat(newValue as DownloadFormat);
  }, []);

  const handleDownloadClick = () => {
    if (!downloadFormat) return;
    handleDownload({
      title,
      columns: columns,
      downloadFormat: downloadFormat,
      includeSourceDocuments,
    });
  };

  return (
    <>
      {loadingDownload ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl font-medium text-gray-700">Preparing your download...</div>
          <Icon className="lucide--loader-circle fa-spin text-brandSecondary text-4xl" />
        </div>
      ) : (
        <div>
          {totalCount !== undefined && (
            <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-700">
              This download will include <span className="font-semibold">{totalCount}</span>{' '}
              {totalCount === 1 ? 'item' : 'items'}
            </div>
          )}
          <div>
            <div className="mb-4">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                File Name
              </Label>
              <Input
                id="title"
                onChange={handleChangeTitle}
                value={title}
                placeholder="Enter a title for your file"
                className="w-full"
              />
            </div>

            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700">Select Columns</Label>

              <MultiSelect values={columns} onValuesChange={handleChangeSelectedColumns}>
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="Select columns" />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  {columnOptions.map((column) => (
                    <MultiSelectItem key={column} value={column}>
                      {column}
                    </MultiSelectItem>
                  ))}
                </MultiSelectContent>
              </MultiSelect>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Format</Label>
              <Select value={downloadFormat} onValueChange={handleChangeSelectedFormat}>
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {showSourceDocumentsOption && (
              <div className="mt-4 flex items-center gap-3">
                <Checkbox
                  id="includeSourceDocuments"
                  checked={includeSourceDocuments}
                  onCheckedChange={(checked) => setIncludeSourceDocuments(Boolean(checked))}
                />
                <label htmlFor="includeSourceDocuments" className="cursor-pointer text-sm text-gray-700">
                  <div className="font-medium">Include source documents</div>
                  <div className="text-xs text-gray-500">
                    Download a zip containing the chronology and all referenced documents
                  </div>
                </label>
              </div>
            )}

            <div className="my-6 border-t border-gray-200"></div>

            <Button onClick={handleDownloadClick} variant="primary" className="w-full">
              Download
              <Icon className="lucide--download" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
